import React, { FC, useEffect, useState } from "react"
import { Form, InputNumber, Switch, Modal, Button, Card, Typography, Space, Popover } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import { observer } from "mobx-react"
import { notifications } from "notifications"
import { useStore } from "stores/utils/use-store"
import { Formula, TestFormulaDto, TestFormulaResponseDto } from "../store/types"
import { testFormula } from "../api/formulas.call"
import { RESERVED_WORDS } from "../constants"
import TextArea from "antd/lib/input/TextArea"
import CodeEditor from "@uiw/react-textarea-code-editor"

const { Title, Text } = Typography

interface TestFormulaModalProps {
  visible: boolean
  onClose: () => void
  formula: Formula | null
}

export const TestFormulaModal: FC<TestFormulaModalProps> = observer(({
  visible,
  onClose,
  formula,
}) => {
  const { dataStore: { formulasStore } } = useStore()
  const [form] = Form.useForm()
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<TestFormulaResponseDto | null>(null)
  const [jsonError, setJsonError] = useState(false)
  const [useMatchDataJsonEditor, setUseMatchDataJsonEditor] = useState(false)

  useEffect(() => {
    if (visible && formula) {
      const matchDataString = "{}"

      form.setFieldsValue({
        formula: formula.formula,
        matchData: matchDataString,
        duration: 0,
        applyMinMaxClamp: formula.applyMinMaxClamp || false,
        minReward: formula.minReward,
        maxReward: formula.maxReward,
        applyBalancer: formula.applyBalancer || false,
        balancer: formula.balancer,
        applyVictoryBonus: formula.applyVictoryBonus || false,
        victoryBonusValue: formula.victoryBonusValue,
        gameModeMultiplier: 1,
        countryMultiplier: 1,
        subscriptionMultiplier: 1,
      })
      setTestResult(null)
      setJsonError(false)
    }
  }, [visible, formula, form])

  const handleTest = async() => {
    const values = form.getFieldsValue()
    setJsonError(false)

    let variables: Record<string, unknown> = {}
    if (values.matchData && values.matchData.trim()) {
      try {
        variables = JSON.parse(values.matchData)
      } catch (error) {
        notifications.error("Invalid JSON syntax in match data field")
        setJsonError(true)

        return
      }
    }

    variables.duration = Number(values.duration) || 0
    variables.victory = variables.victory !== undefined ? variables.victory : true

    const testData: TestFormulaDto = {
      formula: values.formula,
      variables,
      applyMinMaxClamp: values.applyMinMaxClamp || false,
      minReward: values.applyMinMaxClamp && values.minReward !== null && values.minReward !== undefined
        ? Number(values.minReward)
        : undefined,
      maxReward: values.applyMinMaxClamp && values.maxReward !== null && values.maxReward !== undefined
        ? Number(values.maxReward)
        : undefined,
      applyBalancer: values.applyBalancer || false,
      balancer: values.applyBalancer && values.balancer !== null && values.balancer !== undefined
        ? Number(values.balancer)
        : undefined,
      applyVictoryBonus: values.applyVictoryBonus || false,
      victoryBonusValue: values.applyVictoryBonus && values.victoryBonusValue !== null && values.victoryBonusValue !== undefined
        ? Number(values.victoryBonusValue)
        : undefined,
      gameModeMultiplier: values.gameModeMultiplier !== null && values.gameModeMultiplier !== undefined
        ? Number(values.gameModeMultiplier)
        : 1,
      countryMultiplier: values.countryMultiplier !== null && values.countryMultiplier !== undefined
        ? Number(values.countryMultiplier)
        : 1,
      subscriptionMultiplier: values.subscriptionMultiplier !== null && values.subscriptionMultiplier !== undefined
        ? Number(values.subscriptionMultiplier)
        : 1,
    }

    setTesting(true)
    try {
      const result = await testFormula(testData)
      setTestResult(result)
      if (!result.success && result.error) {
        notifications.error(result.error)
      }
    } catch (error: any) {
      notifications.error(error?.cause?.message || error?.message || "Failed to test formula")
      setTestResult({
        success: false,
        error: error?.cause?.message || error?.message || "Failed to test formula",
      })
    } finally {
      setTesting(false)
    }
  }

  const handleClose = () => {
    form.resetFields()
    setTestResult(null)
    setJsonError(false)
    onClose()
  }

  const extractVariablesFromFormula = (formulaStr: string): string[] => {
    const identifierRegex = /\b([a-zA-Z_][a-zA-Z0-9_]*)\b/g

    const matches = formulaStr.match(identifierRegex) || []
    const seen: Record<string, boolean> = {}
    const uniqueVars = matches.filter(v => {
      if (seen[v] || RESERVED_WORDS.includes(v)) return false
      seen[v] = true

      return true
    })

    return uniqueVars
  }

  const functionsPopoverContent = (
    <div style={{ maxWidth: "300px" }}>
      <Text strong>Available Functions:</Text>
      <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: "4px" }}>
        {RESERVED_WORDS.map(func => (
          <code
            key={func}
            style={{
              padding: "2px 6px",
              background: "#f5f5f5",
              borderRadius: "3px",
              fontSize: "12px",
            }}
          >
            {func}
          </code>
        ))}
      </div>
    </div>
  )

  const handleGenerateData = () => {
    const formulaStr = form.getFieldValue("formula")
    if (!formulaStr) {
      notifications.error("Please enter a formula first")

      return
    }

    const variables = extractVariablesFromFormula(formulaStr)
    const matchData: Record<string, number | boolean> = {}
    variables.forEach(v => {
      if (v === "kda") {
        matchData.kills = Math.floor(Math.random() * 10) + 1
        matchData.deaths = Math.floor(Math.random() * 10) + 1
        matchData.assists = Math.floor(Math.random() * 10) + 1
      } else {
        matchData[v] = Math.floor(Math.random() * 10) + 1
      }
    })
    matchData.victory = true

    const matchDataString = JSON.stringify(matchData, null, 2)
    form.setFieldsValue({ matchData: matchDataString })
    form.validateFields(["matchData"])
  }

  return (
    <Modal
      open={visible}
      title="Test Formula"
      width="800px"
      footer={null}
      onCancel={handleClose}
      style={{ top: "10px" }}
    >
      <Form
        form={form}
        layout="vertical"
      >
        <Form.Item
          label={
            <Space>
              <span>Formula</span>
              <Popover
                content={functionsPopoverContent}
                title="Available Functions"
                trigger="hover"
                placement="right"
              >
                <Button
                  type="text"
                  icon={<InfoCircleOutlined />}
                  size="small"
                  style={{ color: "#1890ff", padding: 0, height: "auto" }}
                />
              </Popover>
            </Space>
          }
          name="formula"
          rules={[
            {
              required: true,
              message: "Please enter a formula",
            },
          ]}
        >
          <TextArea rows={3} placeholder="Enter formula expression" />
        </Form.Item>

        <Form.Item
          label={
            <Space>
              <span>Match Data (JSON)</span>
              <Switch
                checkedChildren="JSON Editor"
                unCheckedChildren="Text Editor"
                checked={useMatchDataJsonEditor}
                onChange={setUseMatchDataJsonEditor}
                size="small"
              />
              <Button size="small" onClick={handleGenerateData}>
                Generate Data
              </Button>
            </Space>
          }
          name="matchData"
          rules={[
            {
              required: true,
              message: "Please enter match data",
            },
            {
              validator: (_, value) => {
                if (!value || !value.trim()) {
                  return Promise.reject(new Error("Match data is required"))
                }
                try {
                  JSON.parse(value)
                  setJsonError(false)

                  return Promise.resolve()
                } catch (error) {
                  setJsonError(true)

                  return Promise.reject(new Error("Invalid JSON syntax"))
                }
              },
            },
          ]}
        >
          {useMatchDataJsonEditor ? (
            <CodeEditor
              value={form.getFieldValue("matchData") || ""}
              language="json"
              placeholder='Enter JSON object, e.g., {"score": 100, "kills": 5}'
              onChange={e => {
                form.setFieldsValue({ matchData: e.target.value })
                form.validateFields(["matchData"])
              }}
              padding={15}
              style={{
                fontSize: 14,
                backgroundColor: jsonError ? "#fff2f0" : "#f5f5f5",
                fontFamily: "ui-monospace,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace",
                minHeight: "120px",
              }}
            />
          ) : (
            <TextArea
              rows={4}
              placeholder='Enter JSON object, e.g., {"score": 100, "kills": 5}'
              status={jsonError ? "error" : undefined}
            />
          )}
        </Form.Item>

        <Form.Item
          label="Duration"
          name="duration"
          rules={[
            {
              required: true,
              message: "Please enter duration",
            },
          ]}
        >
          <InputNumber style={{ width: "100%" }} min={0} placeholder="Duration in seconds" />
        </Form.Item>

        <Form.Item
          label="Apply Min/Max Clamp"
          name="applyMinMaxClamp"
          valuePropName="checked"
          initialValue={false}
        >
          <Switch />
        </Form.Item>

        {Form.useWatch("applyMinMaxClamp", form) && (
          <>
            <Form.Item
              label="Min Reward"
              name="minReward"
            >
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>

            <Form.Item
              label="Max Reward"
              name="maxReward"
            >
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
          </>
        )}

        <Form.Item
          label="Apply Balancer"
          name="applyBalancer"
          valuePropName="checked"
          initialValue={false}
        >
          <Switch />
        </Form.Item>

        {Form.useWatch("applyBalancer", form) && (
          <Form.Item
            label="Balancer"
            name="balancer"
          >
            <InputNumber style={{ width: "100%" }} min={0} step={0.01} />
          </Form.Item>
        )}

        <Form.Item
          label="Apply Victory Bonus"
          name="applyVictoryBonus"
          valuePropName="checked"
          initialValue={false}
        >
          <Switch />
        </Form.Item>

        {Form.useWatch("applyVictoryBonus", form) && (
          <Form.Item
            label="Victory Bonus Value"
            name="victoryBonusValue"
          >
            <InputNumber style={{ width: "100%" }} min={0} step={0.01} />
          </Form.Item>
        )}

        <Form.Item
          label="Game Mode Multiplier"
          name="gameModeMultiplier"
          initialValue={1}
        >
          <InputNumber style={{ width: "100%" }} min={0} step={0.01} />
        </Form.Item>

        <Form.Item
          label="Country Multiplier"
          name="countryMultiplier"
          initialValue={1}
        >
          <InputNumber style={{ width: "100%" }} min={0} step={0.01} />
        </Form.Item>

        <Form.Item
          label="Subscription Multiplier"
          name="subscriptionMultiplier"
          initialValue={1}
        >
          <InputNumber style={{ width: "100%" }} min={0} step={0.01} />
        </Form.Item>

        <Space style={{ width: "100%", justifyContent: "space-between", marginTop: 16 }}>
          <Button onClick={handleClose}>
            Close
          </Button>
          <Button
            type="primary"
            onClick={handleTest}
            loading={testing || formulasStore.isLoading}
          >
            Test
          </Button>
        </Space>
      </Form>

      {testResult && (
        <Card
          title="Test Result"
          style={{
            marginTop: 24,
            borderColor: testResult.success ? undefined : "#ff4d4f",
          }}
        >
          {testResult.success ? (
            <Space direction="vertical" style={{ width: "100%" }}>
              {testResult.result !== undefined && (
                <div>
                  <Title level={4}>Result: {testResult.result}</Title>
                </div>
              )}
              {testResult.extractedVariables && testResult.extractedVariables.length > 0 && (
                <div>
                  <Title level={5}>Extracted Variables:</Title>
                  <Text>{testResult.extractedVariables.join(", ")}</Text>
                </div>
              )}
              {testResult.breakdown && (
                <div>
                  <Title level={5}>Breakdown:</Title>
                  <Space direction="vertical" size="small" style={{ width: "100%" }}>
                    {testResult.breakdown.baseReward !== undefined && (
                      <Text>Base Reward: {testResult.breakdown.baseReward}</Text>
                    )}
                    {testResult.breakdown.rewardAfterVictoryBonus !== undefined && (
                      <Text>Reward After Victory Bonus: {testResult.breakdown.rewardAfterVictoryBonus}</Text>
                    )}
                    {testResult.breakdown.rewardAfterMultipliers !== undefined && (
                      <Text>Reward After Multipliers: {testResult.breakdown.rewardAfterMultipliers}</Text>
                    )}
                    {testResult.breakdown.rewardAfterBalancer !== undefined && (
                      <Text>Reward After Balancer: {testResult.breakdown.rewardAfterBalancer}</Text>
                    )}
                    {testResult.breakdown.rewardAfterClamp !== undefined && (
                      <Text>Reward After Clamp: {testResult.breakdown.rewardAfterClamp}</Text>
                    )}
                    {testResult.breakdown.subscriptionMultiplier !== undefined && (
                      <Text>Subscription Multiplier: {testResult.breakdown.subscriptionMultiplier}</Text>
                    )}
                    {testResult.breakdown.boosterReward !== undefined && (
                      <Text>Booster Reward: {testResult.breakdown.boosterReward}</Text>
                    )}
                    {testResult.breakdown.finalReward !== undefined && (
                      <Text strong>Final Reward: {testResult.breakdown.finalReward}</Text>
                    )}
                    {testResult.formulaWithVariables && (
                      <Text>Formula With Variables: {testResult.formulaWithVariables}</Text>
                    )}
                  </Space>
                </div>
              )}
            </Space>
          ) : (
            <Text type="danger">{testResult.error || "Test failed"}</Text>
          )}
        </Card>
      )}
    </Modal>
  )
})
